import styled from "@emotion/styled";
import { Stack } from "@mui/material";
import { Stack as StackIcon } from "@phosphor-icons/react";
import { Button, Notification } from "components/UI";
import { useChurchStore } from "features/churches";
import { StatInfoCard } from "features/dashboard";
import { useDenominationStore } from "features/denominations";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useDebounce } from "react-use";
import { useEmployeesPaginated } from "../api";
import {
  AddEmployeeModal,
  EmployeesTable,
  EmployeesTableHeader,
  GenerateEmployeeFormModal
} from "../components";
import { ImportEmployeesModal } from "../components/Modals/ImportEmployeesModal";
import { useEmployeesStore } from "../store";

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-block: 1rem 2rem;
`;

export const HR = () => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [generateEmployeeFormModalOpen, setGenerateEmployeeFormModalOpen] =
    useState(false);

  const openAddEmployeeModal = useEmployeesStore(
    state => state.openAddEmployeeModal
  );
  const addEmployeeModalOpen = useEmployeesStore(
    state => state.addEmployeeModalOpen
  );
  const closeAddEmployeeModal = useEmployeesStore(
    state => state.closeAddEmployeeModal
  );
  const openImportEmployeesModal = useEmployeesStore(
    state => state.openImportEmployeesModal
  );
  const importEmployeesModalOpen = useEmployeesStore(
    state => state.importEmployeesModalOpen
  );
  const closeImportEmployeesModal = useEmployeesStore(
    state => state.closeImportEmployeesModal
  );

  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const selectedDenomination = useDenominationStore(
    state => state.selectedDenomination
  );

  const { t } = useTranslation();
  const { hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (hash === "#add-employee") {
      openAddEmployeeModal();
    }
  }, [hash, openAddEmployeeModal]);

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    500,
    [search]
  );

  const employeesPaginatedQuery = useEmployeesPaginated({
    churchId: selectedChurch?.id,
    page: 1,
    pageSize: 10,
    isDenomination: window.location.pathname.includes("denomination"),
    denominationId: selectedDenomination?.id,
    queries: {
      search: debouncedSearch
    }
  });

  const isDenominationDashboard = window.location.pathname.includes(
    "denomination-dashboard"
  );

  const handleAddEmployee = () => {
    if (isDenominationDashboard) {
      navigate(`/denomination-dashboard/${selectedDenomination?.id}/hr/create`);
    } else {
      openAddEmployeeModal();
    }
  };

  return (
    <Stack>
      <StatGrid>
        <StatInfoCard
          title={t("HR.HR.TotalEmployees")}
          stat={employeesPaginatedQuery.data?.result?.total_employees ?? 0}
          icon={<StackIcon size={24} />}
          color="rgba(0, 176, 185, 1)"
          background="#82DBD899"
        />
        <StatInfoCard
          title={t("HR.HR.TotalActiveEmployees")}
          stat={
            employeesPaginatedQuery.data?.result?.total_active_employees ?? 0
          }
          icon={<StackIcon size={24} />}
          color="rgba(0, 176, 185, 1)"
          background="#82DBD899"
        />
        <StatInfoCard
          title={t("HR.HR.TotalInactive Employees")}
          stat={
            employeesPaginatedQuery.data?.result?.total_inactive_employees ?? 0
          }
          icon={<StackIcon size={24} />}
          color="rgba(0, 176, 185, 1)"
          background="#82DBD899"
        />
      </StatGrid>
      <Stack spacing={2}>
        <Stack
          direction="row"
          alignItems="center"
          alignSelf="flex-end"
          spacing={1}
        >
          {isDenominationDashboard && (
            <Button onClick={() => setGenerateEmployeeFormModalOpen(true)}>
              {t("HR.HR.GenerateEmployeeForm")}
            </Button>
          )}
          <Button onClick={handleAddEmployee}>{t("HR.HR.AddEmployee")}</Button>
          <Button flavor="outlined" onClick={openImportEmployeesModal}>
            {t("HR.HR.ImportEmployees")}
          </Button>
          {/* <Button flavor="outlined" disabled>
            Export Employees
          </Button> */}
        </Stack>
        <EmployeesTableHeader
          tabValue={0}
          setTabValue={() => {}}
          onSearch={setSearch}
        />
        <EmployeesTable />
      </Stack>
      <AddEmployeeModal
        open={addEmployeeModalOpen}
        onClose={closeAddEmployeeModal}
      />
      <ImportEmployeesModal
        open={importEmployeesModalOpen}
        onClose={closeImportEmployeesModal}
      />
      <GenerateEmployeeFormModal
        open={generateEmployeeFormModalOpen}
        onClose={() => setGenerateEmployeeFormModalOpen(false)}
      />
      <Notification />
    </Stack>
  );
};
